import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useTracking } from '@/hooks/useTracking';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export function extractWorkspaceId(workspaceObj: unknown): string {
  if (!workspaceObj || typeof workspaceObj !== 'object') return '';
  const w = workspaceObj as { id?: string; SK?: string };
  if (w.id) return w.id;
  if (w.SK?.startsWith('WS#')) return w.SK.substring(3);
  if (w.SK) return w.SK;
  return '';
}

async function postCalculateChurn(workspaceId: string, token: string | undefined) {
  const base = getBackendBaseUrl();
  const headers = backendRequestHeaders(token);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token for calculate churn.');
  }

  const { data } = await axios.post<{
    membersUpdated: number;
    workspaceId: string;
    message: string;
  }>(`${base}/workspaces/${workspaceId}/dashboard/calculate-churn`, {}, { headers });

  return data;
}

export function useCalculateChurn() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { trackEvent } = useTracking();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const bearer = resolveBearerToken(sessionJwt);

  return useMutation({
    mutationFn: () => postCalculateChurn(workspaceId, bearer || undefined),
    onSuccess: async (res) => {
      trackEvent('dashboard_churn_recalculated', {
        members_updated: res.membersUpdated,
      });
      await queryClient.invalidateQueries([queryKeys.accountMetrics, workspaceId]);
      await queryClient.refetchQueries([queryKeys.accountMetrics, workspaceId]);
      // Member profile (churn history, scores) caches audience detail per member — refresh after recalc.
      await queryClient.invalidateQueries([queryKeys.users, workspaceId]);
    },
  });
}
